import { defineComponent } from "vue/dist/vue.esm-bundler.js";

function escapeHtml(value = "") {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function renderMarkdownPreview(source = "") {
    const lines = String(source || "").split(/\r?\n/);
    const parts = [];
    let paragraph = [];
    let listItems = [];

    const flushParagraph = () => {
        if (!paragraph.length) return;
        parts.push(`<p>${paragraph.join("<br>")}</p>`);
        paragraph = [];
    };

    const flushList = () => {
        if (!listItems.length) return;
        parts.push(`<ul>${listItems.join("")}</ul>`);
        listItems = [];
    };

    for (const rawLine of lines) {
        const line = String(rawLine || "").trim();
        if (!line) {
            flushParagraph();
            flushList();
            continue;
        }
        if (/^##\s+/.test(line)) {
            flushParagraph();
            flushList();
            parts.push(`<h4>${escapeHtml(line.replace(/^##\s+/, ""))}</h4>`);
            continue;
        }
        if (/^#\s+/.test(line)) {
            flushParagraph();
            flushList();
            parts.push(`<h3>${escapeHtml(line.replace(/^#\s+/, ""))}</h3>`);
            continue;
        }
        if (/^[-*]\s+/.test(line)) {
            flushParagraph();
            listItems.push(`<li>${escapeHtml(line.replace(/^[-*]\s+/, ""))}</li>`);
            continue;
        }
        flushList();
        paragraph.push(escapeHtml(line));
    }

    flushParagraph();
    flushList();
    return parts.join("");
}

export const AssistantHeader = defineComponent({
    name: "AssistantHeader",
    props: {
        bridge: { type: Object, required: true },
        view: { type: Object, required: true }
    },
    setup() {
        return {
            renderMarkdownPreview
        };
    },
    template: `
        <div class="chat-header">
            <div class="chat-header-copy">
                <h2>{{ view.threadTitle }}</h2>
            </div>
            <div class="chat-header-toolbar">
                <button
                    type="button"
                    class="chat-header-btn"
                    title="新規チャット"
                    aria-label="新規チャット"
                    @click="bridge.startNewChat()"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </button>
                <button
                    type="button"
                    class="chat-header-btn task-history-toggle"
                    :class="{ 'is-open': view.taskHistoryOpen }"
                    title="タスクの履歴"
                    aria-label="タスクの履歴"
                    @click="bridge.toggleTaskHistory()"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 8v4l3 3"></path>
                        <path d="M3.05 11a9 9 0 1 1 .5 4m-.5 5v-5h5"></path>
                    </svg>
                </button>
                <button
                    type="button"
                    class="chat-header-btn chat-settings-toggle"
                    :class="{ 'is-open': view.settingsOpen }"
                    title="設定"
                    aria-label="設定"
                    @click="bridge.toggleSettings()"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                </button>
            </div>
        </div>

        <div class="chat-settings" :class="{ 'is-open': view.settingsOpen }">
            <div class="chat-settings-grid">
                <div class="chat-settings-note" style="grid-column: 1 / -1; font-size:12px; color:#667085; line-height:1.5;">
                    AI 接続設定はサーバー側の Environment Variables で管理されます。
                    この画面では PLC プロファイルの選択と編集を行います。
                </div>
                <div class="chat-settings-row" style="grid-template-columns: 1fr;">
                    <label>PLCプロファイル
                        <select :value="view.plcProfileId" @change="bridge.setDialect($event.target.value)">
                            <option v-for="option in view.plcProfileOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                        </select>
                    </label>
                </div>
                <div class="chat-settings-row" style="grid-column: 1 / -1; font-size: 12px; color: #667085;">
                    <span v-if="view.activePlcProfileKind === 'built-in'">現在は built-in profile を選択中です。直接編集はできないため、複製してから調整します。</span>
                    <span v-else>現在は user profile を選択中です。このプロジェクト内で編集内容が保存されます。</span>
                </div>
                <div v-if="view.activePlcProfileBaseLabel" class="chat-settings-row" style="grid-column: 1 / -1; font-size: 12px; color: #667085;">
                    <span>ベース: {{ view.activePlcProfileBaseLabel }}</span>
                </div>
                <div class="chat-settings-row" style="grid-column: 1 / -1; gap: 8px;">
                    <button type="button" class="secondary" @click="bridge.duplicatePlcProfile()">複製</button>
                    <button type="button" class="secondary" :disabled="!view.canEditPlcProfile" @click="bridge.editPlcProfile()">編集</button>
                    <button type="button" class="secondary" :disabled="!view.canEditPlcProfile" @click="bridge.deletePlcProfile()">削除</button>
                </div>
                <div v-if="view.activePlcProfileSummary" class="chat-settings-row plc-profile-summary-card" style="grid-column: 1 / -1; border: 1px solid #eaecf0; border-radius: 10px; padding: 10px; display: grid; gap: 6px; background: #fcfcfd;">
                    <div style="font-size: 12px; font-weight: 700; color: #344054;">現在のプロファイル方針</div>
                    <div style="font-size: 12px; color: #475467; line-height: 1.5;">{{ view.activePlcProfileSummary }}</div>
                    <div
                        v-if="view.activePlcProfileMarkdown"
                        class="plc-profile-markdown-preview"
                        v-html="renderMarkdownPreview(view.activePlcProfileMarkdown)"
                    ></div>
                    <ul v-else-if="view.activePlcProfileRules.length" style="margin: 0; padding-left: 18px; font-size: 12px; color: #475467; line-height: 1.5;">
                        <li v-for="rule in view.activePlcProfileRules" :key="rule">{{ rule }}</li>
                    </ul>
                    <ul v-if="view.activePlcProfileWarnings.length" style="margin: 0; padding-left: 18px; font-size: 12px; color: #b54708; line-height: 1.5;">
                        <li v-for="warning in view.activePlcProfileWarnings" :key="(warning.code || '') + '|' + (warning.path || '') + '|' + (warning.message || warning)">
                            <button v-if="warning.targetLabel" type="button" class="secondary" style="padding: 0; border: 0; background: transparent; color: inherit; font-size: 12px; font-weight: 700; text-decoration: underline; cursor: pointer;" @click="bridge.focusPlcProfileField(warning.path || '')">{{ warning.targetLabel }}:</button>
                            <span>{{ warning.message || warning }}</span>
                        </li>
                    </ul>
                </div>
                <div class="chat-settings-row" style="margin-top: 8px; border-top: 1px solid #eaecf0; padding-top: 8px; grid-column: 1 / -1;">
                    <div style="display:flex; justify-content:space-between; align-items:center; width:100%; font-size:12px; color:#475467;">
                        <span>使用標準仕様 (Skills): <strong>{{ view.skillCount }}件</strong></span>
                        <button
                            type="button"
                            class="secondary"
                            style="padding: 2px 8px; font-size: 11px; height: auto;"
                            @click="bridge.navigateToSkills()"
                        >
                            変更
                        </button>
                    </div>
                </div>
            </div>
            <div class="chat-settings-actions">
                <span>{{ view.settingsSummary }}</span>
                <div style="display:flex;gap:8px;align-items:center;">
                    <button type="button" class="secondary" @click="bridge.exportFlowContext()">FlowContext JSON</button>
                    <button type="button" class="secondary" @click="bridge.exportGenerationIr()">GenerationIR JSON</button>
                    <button type="button" class="secondary" @click="bridge.copyResult()">結果をコピー</button>
                </div>
            </div>
        </div>

        <div
            v-if="view.plcProfileEditorOpen && view.plcProfileDraft"
            class="plc-profile-modal"
            @click.self="bridge.cancelPlcProfileEdit()"
        >
            <div class="plc-profile-modal-dialog" role="dialog" aria-modal="true" aria-label="PLCプロファイル編集">
                <div class="plc-profile-modal-header">
                    <div>
                        <div class="plc-profile-modal-title">PLCプロファイル編集</div>
                        <div class="plc-profile-modal-subtitle">AI規則を中心に、必要なら詳細フィールドも調整できます。</div>
                    </div>
                    <button type="button" class="secondary" @click="bridge.cancelPlcProfileEdit()">閉じる</button>
                </div>
                <div class="plc-profile-editor">
                    <div class="plc-profile-editor-section">
                        <div style="font-size:12px; font-weight:700; color:#344054;">基本情報</div>
                        <label>名前
                            <input type="text" data-plc-profile-field="name" :value="view.plcProfileDraft.name || ''" @input="bridge.updatePlcProfileDraft('name', $event.target.value)">
                        </label>
                        <label>説明
                            <input type="text" data-plc-profile-field="description" :value="view.plcProfileDraft.description || ''" @input="bridge.updatePlcProfileDraft('description', $event.target.value)">
                        </label>
                        <label>AI規則
                            <textarea class="plc-profile-rules-textarea" rows="12" data-plc-profile-field="promptRules" :value="view.plcProfileDraft.promptRules || ''" @input="bridge.updatePlcProfileDraft('promptRules', $event.target.value)"></textarea>
                        </label>
                        <div class="plc-profile-editor-note">
                            ハード構成、I/O方針、命名規則、生成上の注意は基本的にここへまとめて記述します。Markdown 風の見出しや箇条書きで書けます。
                        </div>
                    </div>
                    <details class="plc-profile-advanced">
                        <summary>詳細フィールド（互換用）</summary>
                        <div class="plc-profile-editor-section">
                            <div style="font-size:12px; font-weight:700; color:#344054;">ハード構成</div>
                            <div class="plc-profile-editor-fields two">
                                <label>対象ハード
                                    <input type="text" data-plc-profile-field="hardware.targetName" :value="view.plcProfileDraft.hardware?.targetName || ''" @input="bridge.updatePlcProfileDraft('hardware.targetName', $event.target.value)">
                                </label>
                                <label>コントローラ型式
                                    <input type="text" data-plc-profile-field="hardware.controllerModel" :value="view.plcProfileDraft.hardware?.controllerModel || ''" @input="bridge.updatePlcProfileDraft('hardware.controllerModel', $event.target.value)">
                                </label>
                            </div>
                            <label>I/O構成メモ
                                <textarea rows="3" data-plc-profile-field="hardware.ioSummary" :value="view.plcProfileDraft.hardware?.ioSummary || ''" @input="bridge.updatePlcProfileDraft('hardware.ioSummary', $event.target.value)"></textarea>
                            </label>
                        </div>
                        <div class="plc-profile-editor-section">
                            <div style="font-size:12px; font-weight:700; color:#344054;">生成方針</div>
                            <div class="plc-profile-editor-fields two">
                                <label>宣言ブロック
                                    <select data-plc-profile-field="codegen.declarationBlock" :value="view.plcProfileDraft.codegen?.declarationBlock || 'VAR_GLOBAL'" @change="bridge.updatePlcProfileDraft('codegen.declarationBlock', $event.target.value)">
                                        <option value="VAR_GLOBAL">VAR_GLOBAL</option>
                                        <option value="VAR">VAR</option>
                                    </select>
                                </label>
                                <label>点滅既定(ms)
                                    <input type="number" min="1" step="1" data-plc-profile-field="codegen.defaultBlinkMs" :value="view.plcProfileDraft.codegen?.defaultBlinkMs ?? 500" @input="bridge.updatePlcProfileDraft('codegen.defaultBlinkMs', Number($event.target.value || 500))">
                                </label>
                            </div>
                            <div class="plc-profile-editor-fields two compact">
                                <label><input type="checkbox" data-plc-profile-field="codegen.includeAddressesInDeclarations" :checked="Boolean(view.plcProfileDraft.codegen?.includeAddressesInDeclarations)" @change="bridge.updatePlcProfileDraft('codegen.includeAddressesInDeclarations', $event.target.checked)"> 宣言にアドレスを含める</label>
                                <label><input type="checkbox" data-plc-profile-field="codegen.preferIoMapping" :checked="Boolean(view.plcProfileDraft.codegen?.preferIoMapping)" @change="bridge.updatePlcProfileDraft('codegen.preferIoMapping', $event.target.checked)"> I/O Mapping優先</label>
                            </div>
                        </div>
                        <div class="plc-profile-editor-section">
                            <div style="font-size:12px; font-weight:700; color:#344054;">I/O 方針</div>
                            <div class="plc-profile-editor-fields two compact">
                                <label><input type="checkbox" data-plc-profile-field="ioPolicy.allowAtOnPouInterface" :checked="Boolean(view.plcProfileDraft.ioPolicy?.allowAtOnPouInterface)" @change="bridge.updatePlcProfileDraft('ioPolicy.allowAtOnPouInterface', $event.target.checked)"> POU入出力にAT許可</label>
                                <label><input type="checkbox" data-plc-profile-field="ioPolicy.preferGlobalIoVariables" :checked="Boolean(view.plcProfileDraft.ioPolicy?.preferGlobalIoVariables)" @change="bridge.updatePlcProfileDraft('ioPolicy.preferGlobalIoVariables', $event.target.checked)"> グローバルI/O優先</label>
                            </div>
                            <div class="plc-profile-editor-fields three">
                                <label>入力アドレス prefix
                                    <input type="text" data-plc-profile-field="ioPolicy.inputAddressPrefix" :value="view.plcProfileDraft.ioPolicy?.inputAddressPrefix || ''" @input="bridge.updatePlcProfileDraft('ioPolicy.inputAddressPrefix', $event.target.value)">
                                </label>
                                <label>出力アドレス prefix
                                    <input type="text" data-plc-profile-field="ioPolicy.outputAddressPrefix" :value="view.plcProfileDraft.ioPolicy?.outputAddressPrefix || ''" @input="bridge.updatePlcProfileDraft('ioPolicy.outputAddressPrefix', $event.target.value)">
                                </label>
                                <label>内部アドレス prefix
                                    <input type="text" data-plc-profile-field="ioPolicy.memoryAddressPrefix" :value="view.plcProfileDraft.ioPolicy?.memoryAddressPrefix || ''" @input="bridge.updatePlcProfileDraft('ioPolicy.memoryAddressPrefix', $event.target.value)">
                                </label>
                            </div>
                        </div>
                        <div class="plc-profile-editor-section">
                            <div style="font-size:12px; font-weight:700; color:#344054;">命名規則</div>
                            <div class="plc-profile-editor-fields three">
                                <label>入力prefix
                                    <input type="text" data-plc-profile-field="naming.inputPrefix" :value="view.plcProfileDraft.naming?.inputPrefix || ''" @input="bridge.updatePlcProfileDraft('naming.inputPrefix', $event.target.value)">
                                </label>
                                <label>出力prefix
                                    <input type="text" data-plc-profile-field="naming.outputPrefix" :value="view.plcProfileDraft.naming?.outputPrefix || ''" @input="bridge.updatePlcProfileDraft('naming.outputPrefix', $event.target.value)">
                                </label>
                                <label>内部prefix
                                    <input type="text" data-plc-profile-field="naming.internalPrefix" :value="view.plcProfileDraft.naming?.internalPrefix || ''" @input="bridge.updatePlcProfileDraft('naming.internalPrefix', $event.target.value)">
                                </label>
                            </div>
                        </div>
                    </details>
                    <ul v-if="view.plcProfileEditorHints.length" style="margin: 0; padding-left: 18px; font-size: 12px; color: #b54708; line-height: 1.5;">
                        <li v-for="hint in view.plcProfileEditorHints" :key="hint">{{ hint }}</li>
                    </ul>
                    <div v-if="view.plcProfileEditorError" style="font-size: 12px; color: #b42318;">{{ view.plcProfileEditorError }}</div>
                </div>
                <div class="plc-profile-modal-actions">
                    <button type="button" class="secondary" @click="bridge.cancelPlcProfileEdit()">キャンセル</button>
                    <button type="button" class="primary" @click="bridge.savePlcProfileEdit()">保存</button>
                </div>
            </div>
        </div>
    `
});
